import {
  BYTES_POR_GB,
  type OrganizacaoNaGestao,
  type OrganizacoesNaGestao,
  type ParteDaCriacao,
} from '@cliqcrm/contracts';
import type { TomSelo } from '@/shared/ui';

/**
 * A organização como a lista da gestão a mostra (#765, fase 3, #767).
 *
 * ── O formato que a tela pede ─────────────────────────────────────────────
 *
 * A tela nasceu antes do back (decisão de 26/10/2026) com este formato, e o
 * contrato de `GET /v1/operacao/organizacoes` traz os mesmos dados com os nomes
 * do `/v1`. `organizacaoNaLista` só troca os nomes: nada é calculado no
 * navegador.
 *
 * A busca e o filtro são do servidor: a lista pode ter centenas de
 * organizações, e a tela não remonta no navegador o que o servidor manda
 * pronto. Por isso as contagens dos filtros também vêm de lá.
 */
export type SituacaoDaOrganizacao = 'ativa' | 'em_criacao' | 'suspensa' | 'encerrada';

export type OrganizacaoNaLista = {
  readonly id: string;
  readonly nome: string;
  /** O identificador da organização, o do endereço do login: "rota-sul". */
  readonly identificador: string;
  /** Só os 14 dígitos; nulo na organização criada antes de a criação pedir o cadastro. */
  readonly cnpj: string | null;
  /** O nome do plano do contrato; nulo enquanto a organização não tem contrato. */
  readonly plano: string | null;
  /** Os contratados são nulos enquanto a organização não tem contrato. */
  readonly usuarios: { readonly emUso: number; readonly contratados: number | null };
  /**
   * Em GB. O uso é nulo enquanto a organização não foi medida (#716); o
   * contratado, enquanto ela não tem contrato.
   */
  readonly armazenamento: {
    readonly emUsoGb: number | null;
    readonly contratadoGb: number | null;
  };
  readonly situacao: SituacaoDaOrganizacao;
  /** Quando a organização foi criada, em ISO. */
  readonly desde: string;
  /** Só em criação: a parte onde a criação parou ("Atendimento"). */
  readonly parouEm: string | null;
};

export type ContagensDaLista = {
  /** Quantas organizações o CliqCRM tem, sem busca nem filtro: o número do cabeçalho. */
  readonly total: number;
  /** Quantas a busca encontrou, em todas as situações: o número do "Todas". */
  readonly encontradas: number;
  /** Quantas a busca encontrou em cada situação. */
  readonly porSituacao: Readonly<Record<SituacaoDaOrganizacao, number>>;
};

export const SITUACOES: Readonly<
  Record<
    SituacaoDaOrganizacao,
    { readonly rotulo: string; readonly filtro: string; readonly tom: TomSelo }
  >
> = {
  ativa: { rotulo: 'Ativa', filtro: 'Ativas', tom: 'ok' },
  em_criacao: { rotulo: 'Em criação', filtro: 'Em criação', tom: 'info' },
  suspensa: { rotulo: 'Suspensa', filtro: 'Suspensas', tom: 'atencao' },
  encerrada: { rotulo: 'Encerrada', filtro: 'Encerradas', tom: 'neutro' },
};

/** A ordem dos filtros: da organização em uso para a que saiu. */
export const ORDEM_DAS_SITUACOES: readonly SituacaoDaOrganizacao[] = [
  'ativa',
  'em_criacao',
  'suspensa',
  'encerrada',
];

/** A parte onde a criação parou, com o nome que a tela mostra. */
export const PARTES_DA_CRIACAO: Readonly<Record<ParteDaCriacao, string>> = {
  acesso: 'Acesso',
  cadastro: 'Cadastro',
  atendimento: 'Atendimento',
  administrador: 'Administrador',
};

/** A organização do contrato da API, com os nomes que a tela usa. */
export function organizacaoNaLista(organizacao: OrganizacaoNaGestao): OrganizacaoNaLista {
  return {
    id: organizacao.id,
    nome: organizacao.nome,
    identificador: organizacao.identificador,
    cnpj: organizacao.cnpj,
    plano: organizacao.plano,
    usuarios: {
      emUso: organizacao.usuarios.em_uso,
      contratados: organizacao.usuarios.contratados,
    },
    armazenamento: {
      emUsoGb:
        organizacao.armazenamento_em_uso_bytes === null
          ? null
          : organizacao.armazenamento_em_uso_bytes / BYTES_POR_GB,
      contratadoGb: organizacao.armazenamento_contratado_gb,
    },
    situacao: organizacao.situacao,
    desde: organizacao.desde,
    parouEm: organizacao.parou_em ? PARTES_DA_CRIACAO[organizacao.parou_em] : null,
  };
}

/** As contagens dos filtros, como o servidor as calculou. */
export function contagensDaLista(resposta: OrganizacoesNaGestao): ContagensDaLista {
  return {
    total: resposta.total,
    encontradas: resposta.encontradas,
    porSituacao: resposta.por_situacao,
  };
}
